/**
 * Is a keyword hit NEGATED where it stands? Shared by the study-profile
 * parsers and by EDTS source detection, so "randomisation will not be
 * performed", "Randomization: none", "No central laboratory will be used"
 * and "EHR is not used" all read the same way everywhere.
 *
 * The check is local to the hit's CLAUSE (text between . ; ! ? or a line
 * break), so a negation in another clause ("Randomized; not blinded") does
 * not cancel it. Within the clause a hit is negated when:
 *
 *  - BEFORE it, with at most two words between, stands a negator (not, no, non, un-,
 *    without, never, nor): "no randomisation", "will not use ePRO",
 *    "non-randomized", "not be randomized"; or
 *  - the hit is an item of a LIST that a negator governs (2026-10-31, HVU-SLP-07:
 *    "No central laboratory, IRT, ePRO or other vendor data are used" negates
 *    ePRO, though four words and commas stand between "No" and "ePRO"). The
 *    shape must be a real list, see listNegatorAt: short noun-phrase items (up to
 *    three words, no verbs) joined by commas, closed by "or" / "nor" just before
 *    the LAST item. "No stratification, randomization 1:1", "no dose reduction,
 *    and ePRO will be used" and "No stratification or blocking, randomization by
 *    pharmacy" are NOT lists, so they are not negated. The list must also reach the end of
 *    its clause as a statement of absence: after the last item only nothing, a
 *    short noun, or (under "No" / "neither") "are used" / "are collected" may
 *    follow. A comma splice such as "will not complete paper diaries, ePRO or
 *    eDiary will be used" starts a new positive clause and is not negated, and
 *    under "not" / "never" the first item must be the object of a verb of use.
 *    Lists of prior treatments ("no prior chemotherapy, radiotherapy or ...")
 *    are eligibility, not design, and are not negated. "A, B and C" is not
 *    accepted either: "and" cannot be told from a second clause without reading
 *    verbs, and a missed "no" only leaves today's behaviour; or
 *  - AFTER it comes a negated statement ABOUT it: an auxiliary + not + a verb
 *    of use or performance ("will not be performed", "is not used", "is not
 *    planned"), or a bare negative answer ("Randomization: none", "- N/A",
 *    "not applicable").
 *
 * The after-rule names its verbs on purpose: "Randomization will not be
 * stratified" is still a randomized study. Deliberately conservative the
 * other way too: "Randomized with no stratification" is not negated.
 */

const CLAUSE_END = /[.;!?\n]/;
const CLAUSE_SCAN = 1000;

const BEFORE =
  /(?:^|[^\p{L}\p{N}])(?:not|no|non|un|without|never|nor)(?:[-\s]+[\p{L}\p{N}/-]+){0,2}?[-\s]*$/iu;
const ATTACHED_PREFIX = /(?:non|un)-?$/i;

// ── Lists ───────────────────────────────────────────────────────────────────

const LIST_NEGATOR = /(?<![\p{L}\p{N}])(?:no|not|without|never|neither)(?![\p{L}\p{N}])/giu;
/** "will NOT USE ePRO, IRT or ..." : a verb of use between the negator and the first item. */
const LIST_VERB_LEAD =
  /^\s+(?:be\s+)?(?:use[ds]?|using|include[ds]?|including|employ(?:ed|ing)?|require[ds]?|requiring|collect(?:ed|ing)?|involve[ds]?|involving|utili[sz]e[ds]?)(?![\p{L}\p{N}])/iu;
/** Words that make a phrase a clause, not a list item: verbs, prepositions, conjunctions. */
const LIST_STOP = new Set(
  ("is are was were be been being will would shall should can could may might must do does did has have had use used using and or nor but if when then than that which who where while because " +
    "randomized randomised performed required planned conducted collected allowed available done provided included implemented applicable occurred happened with for in on at by per from to as via").split(" "),
);
const listWord = (w: string) => /^[\p{L}\p{N}/'’-]+$/u.test(w);
const isStop = (w: string) => LIST_STOP.has(w.toLowerCase());
/** A word that reads as a past verb ("recorded", "discontinued"): never the end of a list item or a trailing noun. */
const endsInEd = (w: string) => /\p{L}{2,}ed$/iu.test(w);
/** One to three plain words, none of them a verb, preposition or conjunction. */
function isListItem(s: string): boolean {
  const words = s.trim().split(/\s+/).filter(Boolean);
  return words.length >= 1 && words.length <= 3 && words.every((w) => listWord(w) && !isStop(w)) && !endsInEd(words[words.length - 1]);
}
/** Up to two plain words before the hit within its own item ("a", "central"). */
function isItemLead(s: string): boolean {
  const words = s.trim().split(/\s+/).filter(Boolean);
  return words.length <= 2 && words.every((w) => listWord(w) && !isStop(w));
}
/** What follows the hit inside its own item (nothing, or one plain letters-only word), and then the rest of the clause; null when it is not plain. */
function restAfterItem(after: string): string | null {
  const t = after.trim();
  if (!t || t.startsWith(",")) return t;
  const words = t.split(/\s+/);
  if (isStop(words[0].replace(/,$/, ""))) return t;
  if (!/^\p{L}+$/u.test(words[0]) || endsInEd(words[0])) return null;
  return words.slice(1).join(" ");
}
/**
 * The list closes AFTER the hit: optional rest of the hit's item, more items
 * separated by commas, then "or" / "nor" and a last item. Returns what is left
 * of the clause after that last item (the empty string when nothing), or null.
 */
function listClosesAfter(after: string): string | null {
  const c = /(?:,\s*|\s+)(or|nor)\s+/i.exec(after);
  if (!c) return null;
  const segs = after.slice(0, c.index).split(",");
  const rest = segs[0].trim();
  if (rest && !(/^\p{L}+$/u.test(rest) && !isStop(rest) && !endsInEd(rest))) return null;
  if (!segs.slice(1).every((x) => isListItem(x))) return null;
  const tokens = after.slice(c.index + c[0].length).trim().split(/\s+/).filter(Boolean);
  let taken = 0;
  let comma = false;
  for (const w of tokens) {
    const bare = w.replace(/,$/, "");
    if (!bare || !listWord(bare) || isStop(bare) || taken === 3) break;
    taken++;
    if (bare !== w) {
      comma = true;
      break;
    }
  }
  if (taken < 1 || endsInEd(tokens[taken - 1].replace(/,$/, ""))) return null;
  return `${comma ? ", " : ""}${tokens.slice(taken).join(" ")}`.trim();
}

/** Hard bound, so long period-free text cannot make the list read slow: items are three words, so a list is short. The window alone bounds the number of negators looked at. */
const LIST_WINDOW = 300;
/** Eligibility and history wording: a list of prior treatments is about participants, not the design. */
const LIST_HISTORY =
  /(?<![\p{L}\p{N}])(?:prior|previous|previously|received|history|treated|had|before|past|recent|recently)(?![\p{L}\p{N}])/iu;
/** A predicate that says the list is unused or absent. "will be provided / completed / assign" is a NEW positive clause, so it is not here. */
const LIST_PRED_NO =
  /^(?:(?:is|are|will\s+be|shall\s+be|would\s+be)\s+)?(?:used|employed|utili[sz]ed|collected|required|needed|applicable)(?:\s+(?:in|for)\s+(?:this|the)\s+(?:study|trial))?$/iu;
const isPlainWords = (t: string, max: number) => {
  const words = t.trim().split(/\s+/).filter(Boolean);
  return words.length <= max && words.every((w) => listWord(w) && !isStop(w) && !endsInEd(w));
};
/**
 * What may follow the last item. A list reaches the end of its clause, so under
 * "no" it may be followed by a predicate that says the list is unused ("are
 * used", "are collected") or by a short noun ("endpoints"). Under "without" only
 * by a short noun, under "neither" only by that predicate, and under "not USE
 * ..." by nothing: "will not complete paper diaries, ePRO or eDiary will be used"
 * is a comma splice and starts a new positive clause.
 */
function trailingOk(head: string, trailing: string): boolean {
  const t = trailing.trim();
  if (!t) return true;
  if (head === "no") return LIST_PRED_NO.test(t) || isPlainWords(t, 2);
  if (head === "without") return isPlainWords(t, 2);
  if (head === "neither") return LIST_PRED_NO.test(t);
  return false;
}

/**
 * The hit is an item of a list governed by a negator earlier in the clause.
 * Reads the text BEFORE the hit for the negator and the items already passed
 * (separated by plain commas only), and either the closing "or" / "nor" before
 * the hit (the hit is the last item) or, when the hit is not last, the closing
 * "or" / "nor" AFTER it; then what is left of the clause must read as unused
 * (trailingOk). Looks back at most LIST_WINDOW characters, nearest negator
 * first. Returns the negator's offset, or -1.
 */
function listNegatorAt(before: string, after: string): number {
  if (after.length > LIST_WINDOW) return -1;
  let offset = 0;
  if (before.length > LIST_WINDOW) {
    const cut = before.length - LIST_WINDOW;
    const sp = before.indexOf(" ", cut);
    offset = sp < 0 ? before.length : sp + 1;
  }
  const win = before.slice(offset);
  const negs = [...win.matchAll(LIST_NEGATOR)];
  for (let n = negs.length - 1; n >= 0; n--) {
    const m = negs[n];
    const head = m[0].toLowerCase();
    let seg = win.slice((m.index ?? 0) + m[0].length);
    if (head === "not" || head === "never") {
      // "will not USE a, b or c": the first item is the object of a verb of use. Anything else after "not" is a clause.
      if (!LIST_VERB_LEAD.test(seg)) continue;
      seg = seg.replace(LIST_VERB_LEAD, "");
    }
    if (!/^\s/.test(seg)) continue;
    if (LIST_HISTORY.test(seg) || LIST_HISTORY.test(after)) continue;
    const parts = seg.trimStart().split(",");
    const lastRaw = parts.pop() ?? "";
    if (!parts.every((x) => isListItem(x))) continue;
    const closing = /^(?:(.*?)\s+)?(?:or|nor)\s+(.*)$/is.exec(lastRaw.trimStart());
    let trailing: string | null;
    if (closing) {
      // The hit is the last item: "..., or ePRO" / "A or ePRO".
      if (closing[1] !== undefined && !isListItem(closing[1])) continue;
      if (!isItemLead(closing[2])) continue;
      trailing = restAfterItem(after);
    } else {
      if (!isItemLead(lastRaw)) continue;
      // Not the last item: the list must close after the hit.
      trailing = listClosesAfter(after);
    }
    if (trailing !== null && trailingOk(head, trailing)) return offset + (m.index ?? 0);
  }
  return -1;
}

const USE_VERBS =
  "(?:performed|used|utili[sz]ed|employed|planned|applicable|done|conducted|required|collected|implemented|involved|included|available|provided|randomi[sz]ed|use|used)";
const AFTER_AUX = new RegExp(
  `^[\\p{L}\\p{N}]*\\s*(?:\\([^)]*\\)\\s*)?(?:will|is|are|was|were|shall|has|have|does|do|did|can|could|would|should)\\s+(?:not|never)\\s+(?:be\\s+|been\\s+)?${USE_VERBS}(?![\\p{L}\\p{N}])`,
  "iu",
);
const AFTER_ANSWER =
  /^[\p{L}\p{N}]*\s*(?:\([^)]*\)\s*)?[:=\-–—]?\s*(?:none|n\/?a|not\s+applicable|no|not\s+used|not\s+performed|not\s+planned)(?![\p{L}\p{N}])/iu;

/**
 * A "not" that describes a SUBGROUP of participants rather than the study
 * design: "subjects who are not randomized within 28 days", "participants not
 * eligible for randomization", "those not randomized". Such a clause says the
 * study HAS randomization for others, so it is never a design-level "no".
 */
const SUBGROUP_BEFORE_NEGATOR =
  /(?:^|[^\p{L}\p{N}])(?:(?:who|whom|that|which)(?:\s+(?:are|is|were|was|will|would|could|can|do|does|did|have|has|had|may|might|shall|should)(?:\s+(?:be|been|have))?)?|those|anyone|participants?|subjects?|patients?|individuals?|people|persons?|children|women|men|volunteers?)\s*$/iu;
/** Words right after the negator that make it about eligibility, not design. */
const SUBGROUP_AFTER_NEGATOR = /^(?:not|no|never)\s+(?:yet\s+)?(?:eligible|qualif\p{L}*|selected|chosen|suitable|able)(?![\p{L}\p{N}])/iu;
/** "not only X but also Y" is emphasis, not negation. */
const NOT_ONLY = /^not\s+only(?![\p{L}\p{N}])/iu;

export type NegationKind = "negated" | "subgroup" | null;

/**
 * How the hit at [idx, idx+len) in `text` is negated within its clause:
 * `negated` (the design does not have it), `subgroup` (a "not" about some
 * participants, which implies the study does have it), or null.
 */
export function negationAt(text: string, idx: number, len: number): NegationKind {
  // The clause is read at most CLAUSE_SCAN non-whitespace characters either side of the hit (whitespace runs, which
  // flattened PDF tables contain, are free), cut at a space: the rules below look only at the words next to the hit, and an
  // unbounded scan made period-free text quadratic. A single "word" over CLAUSE_SCAN characters is cut: accepted.
  let start = idx;
  let seen = 0;
  while (start > 0 && seen < CLAUSE_SCAN && !CLAUSE_END.test(text[start - 1])) {
    start--;
    if (!/\s/.test(text[start])) seen++;
  }
  if (start > 0 && seen >= CLAUSE_SCAN && !CLAUSE_END.test(text[start - 1]) && !/\s/.test(text[start - 1])) {
    const sp = text.slice(start, idx).search(/\s/);
    start = sp < 0 ? idx : start + sp + 1;
  }
  let end = idx + len;
  seen = 0;
  while (end < text.length && seen < CLAUSE_SCAN && !CLAUSE_END.test(text[end])) {
    if (!/\s/.test(text[end])) seen++;
    end++;
  }
  if (end < text.length && seen >= CLAUSE_SCAN && !CLAUSE_END.test(text[end]) && !/\s/.test(text[end])) {
    const t = text.slice(idx + len, end);
    const sp = Math.max(t.lastIndexOf(" "), t.lastIndexOf("\t"));
    end = sp < 0 ? idx + len : idx + len + sp;
  }
  const before = text.slice(start, idx);
  const after = text.slice(idx + len, end);

  if (ATTACHED_PREFIX.test(before)) return "negated";
  const m = BEFORE.exec(before);
  if (m) {
    const lead = /^[^\p{L}\p{N}]/u.test(m[0]) ? 1 : 0;
    const negatorOnward = m[0].slice(lead);
    const preceding = before.slice(0, m.index + lead);
    if (NOT_ONLY.test(negatorOnward)) return null;
    if (SUBGROUP_BEFORE_NEGATOR.test(preceding) || SUBGROUP_AFTER_NEGATOR.test(negatorOnward)) return "subgroup";
    return "negated";
  }
  const listAt = listNegatorAt(before, after);
  if (listAt >= 0) {
    const negatorOnward = before.slice(listAt);
    // No NOT_ONLY test here: "not only" fails the verb-of-use lead, so it can never head a list.
    if (SUBGROUP_BEFORE_NEGATOR.test(before.slice(0, listAt)) || SUBGROUP_AFTER_NEGATOR.test(negatorOnward)) return "subgroup";
    return "negated";
  }
  if (AFTER_AUX.test(after)) return "negated";
  if (AFTER_ANSWER.test(after)) return "negated";
  return null;
}

/** Whether the hit is negated at DESIGN level (a subgroup "not" is not). */
export function isNegatedAt(text: string, idx: number, len: number): boolean {
  return negationAt(text, idx, len) === "negated";
}

/**
 * Local-lab / site-read wording in the hit's clause. Used to stop a GENERIC
 * lab or imaging keyword ("hematology", "MRI") from implying a CENTRAL lab or
 * central imaging when the clause says the work is done at the site.
 */
const LOCAL_WORDING =
  /(?:^|[^\p{L}\p{N}])(?:local(?:ly)?|at\s+(?:each|the|every|participating)?\s*sites?|site[-\s]?(?:read|level|based)|read\s+by\s+(?:the\s+)?(?:site|investigator|local)|by\s+the\s+(?:site|investigator)|in[-\s]house|on[-\s]site)(?![\p{L}\p{N}])/iu;

export function clauseSaysLocal(text: string, idx: number, len: number): boolean {
  let start = idx;
  while (start > 0 && !CLAUSE_END.test(text[start - 1])) start--;
  let end = idx + len;
  while (end < text.length && !CLAUSE_END.test(text[end])) end++;
  return LOCAL_WORDING.test(text.slice(start, end));
}
